// Shared shape for the whole signup-password wizard (Personal info -> Set
// password -> Check details). One react-hook-form instance spans all three
// sub-steps — nothing is submitted to Auth0 until Join (sub-step 3) fires a
// single signup() call. See ARCHITECTURE.md "Member Signup Flow".
export interface WizardFormValues {
  gender: string;
  title: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  mobile: string;
  country: string;
  residentialAddress: string;
  postalSameAsResidential: boolean;
  postalAddress: string;
  tfn: string;
  password: string;
  confirmPassword: string;
  consentAccepted: boolean;
  // Present so <Captcha> has a form path to bind to. Only the legacy `auth0`
  // image captcha actually writes here (SimpleCaptchaWidget registers it); the
  // widget providers leave it "" and report their token through useCaptcha
  // instead, which is why SignupPasswordForm reads `captchaValue` rather than
  // this field. Deliberately excluded from PERSONAL_INFO_FIELDS and
  // PASSWORD_FIELDS so no sub-step's `trigger()` validates it.
  captcha: string;
}

export const WIZARD_DEFAULT_VALUES: WizardFormValues = {
  gender: "",
  title: "",
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  mobile: "",
  country: "Australia",
  residentialAddress: "",
  postalSameAsResidential: true,
  postalAddress: "",
  tfn: "",
  password: "",
  confirmPassword: "",
  consentAccepted: false,
  captcha: "",
};

export const GENDER_OPTIONS = ["Female", "Male", "Non-binary", "Prefer not to say"];
export const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Mx", "Dr"];

export const PERSONAL_INFO_FIELDS: (keyof WizardFormValues)[] = [
  "gender",
  "title",
  "firstName",
  "lastName",
  "dateOfBirth",
  "mobile",
  "country",
  "residentialAddress",
  "postalAddress",
  "tfn",
];

export const PASSWORD_FIELDS: (keyof WizardFormValues)[] = ["password", "confirmPassword"];

// Flow B collects identity essentials only; everything else is deferred to the
// progressive-profiling form, where DOB is mandatory. See ARCHITECTURE.md
// "Signup Flows (A / B / C)".
//
// Deliberately NOT including `consentAccepted`: the step-5 declaration is part
// of Flow A's "Check details" review, which Flow B does not have. The
// declaration is collected by the progressive-profiling form instead, and
// complete-profile requires it before a member can reach COMPLETE — so
// "complete" still means the same thing in both flows.
export const FLOW_B_PERSONAL_INFO_FIELDS: (keyof WizardFormValues)[] = [
  "firstName",
  "lastName",
  "mobile",
];

export function personalInfoFieldsForFlow(
  flow: "A" | "B"
): (keyof WizardFormValues)[] {
  return flow === "B" ? FLOW_B_PERSONAL_INFO_FIELDS : PERSONAL_INFO_FIELDS;
}

// DOB sanity: a real, past date, and old enough to hold a super account.
// Not specified precisely in the brief — 18 is the reasonable adult-product
// floor for a superannuation fund, applied here as a client-side UX check
// only (the server-side Action does not re-enforce a minimum age).
export function isValidPastAdultDate(value: string): boolean {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  if (date > now) return false;
  const eighteenYearsAgo = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
  return date <= eighteenYearsAgo;
}
